import React from 'react';
import type { FunnelInsightsApiClient } from '../../api/insightsClient';
import {
  CREATIVE_REPORT_ENRICHMENT_MAX_KEYS,
  type CreativeReportEnrichmentItem,
} from '../../creativeDecisions';

interface UseCreativeReportEnrichmentRequest {
  client: Pick<FunnelInsightsApiClient, 'getCreativeDecisionEnrichment'>;
  projectId: string;
  deploymentKeys: readonly string[];
  enabled: boolean;
  /** Changes when the report scope is replaced; load-more alone does not. */
  scopeKey: string;
}

interface UseCreativeReportEnrichmentResult {
  byDeploymentKey: Readonly<Record<string, CreativeReportEnrichmentItem | undefined>>;
  loading: boolean;
  error: string | null;
}

/**
 * Lazy optional projection over report rows. Failures only affect creative
 * previews: the caller's metric rows remain untouched and fully usable.
 */
export function useCreativeReportEnrichment({
  client,
  projectId,
  deploymentKeys,
  enabled,
  scopeKey,
}: UseCreativeReportEnrichmentRequest): UseCreativeReportEnrichmentResult {
  const cacheRef = React.useRef(new Map<string, CreativeReportEnrichmentItem>());
  const activeScopeRef = React.useRef(scopeKey);
  const [byDeploymentKey, setByDeploymentKey] = React.useState<Readonly<Record<string, CreativeReportEnrichmentItem>>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const uniqueKeys = React.useMemo(
    () => Array.from(new Set(deploymentKeys.map((value) => value.trim()).filter(Boolean))),
    [deploymentKeys],
  );

  React.useEffect(() => {
    if (activeScopeRef.current === scopeKey) return;
    activeScopeRef.current = scopeKey;
    cacheRef.current.clear();
    setByDeploymentKey({});
    setError(null);
  }, [scopeKey]);

  React.useEffect(() => {
    if (!enabled || !projectId || uniqueKeys.length === 0) {
      setLoading(false);
      return undefined;
    }
    const missing = uniqueKeys.filter((deploymentKey) => !cacheRef.current.has(deploymentKey));
    if (missing.length === 0) return undefined;
    const controller = new AbortController();
    const chunks = chunk(missing, CREATIVE_REPORT_ENRICHMENT_MAX_KEYS);
    setLoading(true);
    setError(null);
    void Promise.allSettled(chunks.map(async (deploymentKeysChunk) => {
      const response = await client.getCreativeDecisionEnrichment({
        projectId,
        deploymentKeys: deploymentKeysChunk,
        signal: controller.signal,
      });
      for (const item of response.data.items) cacheRef.current.set(item.deploymentKey, item);
    })).then((results) => {
      if (controller.signal.aborted) return;
      setByDeploymentKey(Object.fromEntries(cacheRef.current));
      const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (failed) {
        // Successful chunks are still safe to show; one failed chunk never
        // removes a metric row or a previously resolved creative.
        setError(failed.reason instanceof Error ? failed.reason.message : String(failed.reason));
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [client, enabled, projectId, scopeKey, uniqueKeys]);

  return { byDeploymentKey, loading, error };
}

function chunk<T>(values: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}
